import React, { useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Trans } from 'react-i18next';
import { IsFocusVisibleClassName, memoizeFunction, mergeStyles, Stack } from '@fluentui/react';
import { useBoolean } from '@fluentui/react-hooks';
import { Dismiss16Regular } from '@fluentui/react-icons';
import { microsoftConsumerSecurityClient } from 'client-factories/MicrosoftConsumerSecurityClientFactoryV1';
import clsx from 'clsx';
import { IconButton } from 'common/components/Buttons/v8';
import { Link } from 'common/components/Link';
import { Text } from 'common/components/Text';
import { useScreen } from 'common/hooks/useScreen';
import { type IAppTheme, useAppTheme } from 'common/theme/AppTheme';
import { isClarityEnabled } from 'common/utilities/FlightSetting';
import { pageDataService } from 'common/utilities/PageDataService';
import { useLogViewOnCondition } from 'common/utilities/telemetryHelpers';
import { MicrosoftPrivacyURL } from 'common/utilities/URLConstants';
import { useLocalizedStrings } from 'i18n';

const getClassName = memoizeFunction(({ palette, effects }: IAppTheme, isMobileScreen: boolean) => {
  const margin = 10;

  return mergeStyles({
    position: 'fixed',
    bottom: 0,
    left: '50%',
    transform: `translateX(-50%) translateX(-${margin}px)`,
    width: 1000,
    maxWidth: '100%',
    backgroundColor: palette.white,
    padding: isMobileScreen ? 16 : '24px 32px',
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    boxShadow: effects.elevation16,
    margin: `0 ${margin}px`,
    zIndex: 10,
  });
});

/**
 * Fixed banner at bottom of page to display use of Clarity notice
 */
export const ClarityNoticeBanner: React.FC = () => {
  const theme = useAppTheme();
  const { isMobileScreen } = useScreen();
  const className = getClassName(theme, isMobileScreen);
  const { t } = useLocalizedStrings();

  const [isNoticeDismissed, { setTrue: dismissNotice }] = useBoolean(false);
  const onDismissClick = useCallback(() => {
    dismissNotice();
    microsoftConsumerSecurityClient.userProfile.patchUserProfile({
      body: {
        isClarityNoticeDismissed: true,
      },
    });
  }, [dismissNotice]);

  const shouldShowClarityNotice = isClarityEnabled() && !isNoticeDismissed && !pageDataService.isClarityNoticeDismissed();
  useLogViewOnCondition('ClarityNoticeBannerShown', shouldShowClarityNotice);

  if (!shouldShowClarityNotice) {
    return null;
  }

  const notice = (
    <Trans
      i18nKey="ClarityNotice.Text"
      components={{
        privacyLink: (
          <Link
            href={MicrosoftPrivacyURL}
            target="_blank"
            aria-label={t('ClarityNotice.PrivacyLinkAriaLabel')}
            telemetryEventName="ClarityNoticeBannerPrivacyLink"
          />
        ),
      }}
      t={t}
    />
  );

  const DismissButton = <Dismiss16Regular />;

  return ReactDOM.createPortal(
    <Stack horizontal className={clsx(className, IsFocusVisibleClassName)} verticalAlign="start" tokens={{ childrenGap: 5 }}>
      <Text variant="Body1">{notice}</Text>
      <Stack.Item>
        <IconButton
          ariaLabel={t('ClarityNotice.CloseButtonAriaLabel')}
          onRenderIcon={() => DismissButton}
          onClick={onDismissClick}
          telemetryEventName="ClarityNoticeBannerDismiss"
        />
      </Stack.Item>
    </Stack>,
    document.body
  );
};

export default ClarityNoticeBanner;
